import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, CreditCard, ShieldCheck, Compass, ArrowRight } from "lucide-react";
import RALayout from "../components/ra/RALayout";
import { getBooking, payBooking } from "../lib/adventureApi";

const money = (n) => `A$${Number(n || 0).toLocaleString()}`;

export default function BookingStatus() {
  const { ref } = useParams();
  const [b, setB] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => getBooking(ref).then(setB).catch(() => setNotFound(true));
  useEffect(() => { load(); }, [ref]);

  const pay = async (payment_type) => {
    setBusy(true);
    try {
      const r = await payBooking(ref, { payment_type, origin_url: window.location.origin });
      if (r.checkout_url) { window.location.href = r.checkout_url; return; }
      toast.error("Couldn't start payment");
    } catch { toast.error("Couldn't start payment"); }
    finally { setBusy(false); }
  };

  if (notFound) return (
    <RALayout><div className="py-28 text-center max-w-md mx-auto px-6"><Compass className="w-10 h-10 text-terracotta mx-auto" /><h1 className="font-display font-extrabold text-2xl mt-4">Booking not found</h1></div></RALayout>
  );
  if (!b) return <RALayout><div className="py-28 text-center text-ink-secondary"><Loader2 className="w-6 h-6 animate-spin text-terracotta inline" /></div></RALayout>;

  const depositPaid = !!b.deposit_paid_at;
  const confirmed = b.status === "confirmed";
  const refunded = b.status === "cancelled_refunded";

  return (
    <RALayout>
      <div className="max-w-2xl mx-auto px-5 py-14" data-testid="booking-status-page">
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-terracotta">Your booking · {b.ref}</p>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mt-2">{b.trip_name}</h1>
        <p className="text-ink-secondary mt-1">{b.destination ? `${b.destination} · ` : ""}{b.travellers} traveller{b.travellers > 1 ? "s" : ""} · total {money(b.booking_value)}</p>

        <div className="mt-8 rounded-xl border border-sand-dark bg-white divide-y divide-sand">
          <Step label="Deposit" amount={b.deposit_amount} done={depositPaid} testid="step-deposit" />
          <Step label="Balance" amount={b.balance_amount} done={confirmed} testid="step-balance" />
        </div>

        <div className="mt-6" data-testid="booking-action">
          {refunded ? (
            <p className="text-terracotta font-semibold">This booking was cancelled/refunded.</p>
          ) : confirmed ? (
            <div className="rounded-xl bg-moss-light border border-moss/30 p-6 text-center" data-testid="booking-confirmed">
              <Check className="w-9 h-9 text-moss mx-auto" />
              <h2 className="font-display font-extrabold text-xl mt-2">You're fully booked!</h2>
              <p className="text-ink-secondary mt-1">Thank you — your Responsible Adventures journey is confirmed.</p>
            </div>
          ) : !depositPaid ? (
            <button onClick={() => pay("deposit")} disabled={busy} data-testid="pay-deposit-btn"
              className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay {money(b.deposit_amount)} deposit now
            </button>
          ) : (
            <button onClick={() => pay("balance")} disabled={busy || b.balance_amount <= 0} data-testid="pay-balance-btn"
              className="w-full h-12 rounded-sm bg-terracotta hover:bg-terracotta-hover text-white font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Pay {money(b.balance_amount)} balance
            </button>
          )}
          <p className="text-[11px] text-ink-muted mt-3 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> Secured by Stripe · payments to Responsible Adventures Australia</p>
        </div>

        <Link to="/tours" className="mt-8 inline-flex items-center gap-2 text-moss font-semibold text-sm">Explore more adventures <ArrowRight className="w-4 h-4" /></Link>
      </div>
    </RALayout>
  );
}

function Step({ label, amount, done, testid }) {
  return (
    <div className="flex items-center justify-between px-5 py-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${done ? "bg-moss text-white" : "bg-sand text-ink-muted"}`}>
          {done ? <Check className="w-4 h-4" /> : <CreditCard className="w-4 h-4" />}
        </div>
        <div><p className="font-display font-bold text-ink">{label}</p><p className="text-xs text-ink-muted">{done ? "Paid" : "Due"}</p></div>
      </div>
      <p className="font-display font-bold tabular-nums">A${Number(amount || 0).toLocaleString()}</p>
    </div>
  );
}
